/**
 * The wire contract for an issue report (REPORT-SPEC §5.2, §6, §8).
 *
 * One module for the enums and bounds because three consumers must agree
 * exactly: the browser that builds a request, the Lambda that validates it, and
 * the operator CLI that reads what was stored. A second copy of the problem-type
 * list is how a checkbox ships that the server answers 400 to — invisible in
 * development, where nobody clicks every box.
 *
 * ## Why the order of `PROBLEM_TYPES` is load-bearing
 *
 * §8.2.9 stores the selected values in *this* order regardless of click order,
 * and §8.4 hashes what was stored. Two people reporting the same two problems in
 * opposite orders must produce the same canonical document, or the digest that
 * makes a retry idempotent would depend on which checkbox was ticked first.
 *
 * Reordering this array is therefore a schema change, not a display change. If
 * the dialog ever wants a different visual order, it sorts for display and
 * submits in this one.
 */

/**
 * §5.2's stable wire values, in canonical order.
 *
 * `looks_wrong` is first because it is the one a person reaches for when they
 * cannot name the failure, and §2.2 marks it as a decision not to be simplified
 * away: the feature exists partly for readers who know a number is wrong without
 * knowing which of our terms describes it.
 */
export const PROBLEM_TYPES = [
  "looks_wrong",
  "anatomy_map_wrong",
  "tensor_group_missing",
  "tensor_group_wrong",
  "parameter_count_wrong",
  "byte_count_wrong",
  "bpw_wrong",
  "dtype_quantization_wrong",
  "tensor_detail_wrong",
  "comparison_wrong",
  "other",
] as const;

export type ProblemType = (typeof PROBLEM_TYPES)[number];

/** The broad flag, valid only alone (§8.2.9). Named so the rule reads. */
export const BROAD_PROBLEM_TYPE: ProblemType = "looks_wrong";

const PROBLEM_RANK = new Map<string, number>(PROBLEM_TYPES.map((t, i) => [t, i]));

export function isProblemType(value: unknown): value is ProblemType {
  return typeof value === "string" && PROBLEM_RANK.has(value);
}

/**
 * Canonical order, from whatever order they arrived in.
 *
 * Sorted by position in `PROBLEM_TYPES`, never alphabetically: the enum's order
 * is a deliberate reading order (broad first, then specific, then `other` last)
 * and alphabetising it would put `anatomy_map_wrong` above `looks_wrong` for no
 * reason anyone could reconstruct later.
 */
export function canonicalProblemTypes(types: readonly ProblemType[]): ProblemType[] {
  return [...types].sort(
    (a, b) => (PROBLEM_RANK.get(a) ?? 0) - (PROBLEM_RANK.get(b) ?? 0),
  );
}

/** §5.2: at least one, at most four distinct values. */
export const MIN_PROBLEM_TYPES = 1;
export const MAX_PROBLEM_TYPES = 4;

/** §6: the metric the map is coloured by when the report is filed. */
export const METRICS = ["dtype", "bpw", "bytes", "params"] as const;
export type ReportMetric = (typeof METRICS)[number];

/** §6: which reader produced the view. */
export const FORMATS = ["safetensors", "gguf"] as const;
export type ReportFormat = (typeof FORMATS)[number];

/** §9.2's lifecycle, as one enum. §9.4 explains why it is not four booleans. */
export const STATUSES = ["NEW", "CONFIRMED", "REJECTED", "RESOLVED"] as const;
export type ReportStatus = (typeof STATUSES)[number];

/**
 * Field bounds, in code units (§5.3's measure), from §8.2.
 *
 * `details` is 512 because that is what the textarea's `maxlength` enforces, and
 * the two must be the same number measured the same way or the client's counter
 * disagrees with the server's rejection.
 */
export const LIMITS = {
  details: 512,
  part: 256,
  partKey: 256,
  tower: 128,
  selectedTensor: 512,
  siteBuild: 80,
  /** §8.2.3. WAF enforces the same bound first; this is the backstop. */
  bodyBytes: 8192,
} as const;

/** §8.2.12's conservative allowlist for a build identifier. */
export const SITE_BUILD_PATTERN = /^[A-Za-z0-9._+-]{1,80}$/;

/** §8.2.8. Always a commit, never a branch — that is what pins a report. */
export const COMMIT_SHA_PATTERN = /^[0-9a-f]{40}$/;

/** §8.2.6. Canonical lowercase UUID v4, as `crypto.randomUUID` emits. */
export const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** The only schema this build understands (§8.1). */
export const SCHEMA_VERSION = 1;

/**
 * The request's closed key set (§8.1: "reject unknown fields at every level").
 *
 * Listed rather than derived from a type, because the check has to happen at run
 * time against an attacker-controlled object and TypeScript is gone by then.
 */
export const REQUEST_KEYS = [
  "schemaVersion",
  "submissionId",
  "repoId",
  "revisionSha",
  "problemTypes",
  "details",
  "context",
] as const;

export const CONTEXT_KEYS = [
  "siteBuild",
  "format",
  "metric",
  "part",
  "partKey",
  "tower",
  "selectedTensor",
  "comparison",
] as const;

export const COMPARISON_KEYS = ["repoId", "revisionSha"] as const;

/** The comparison model, when a completed compare was on screen (§6). */
export interface ReportComparison {
  readonly repoId: string;
  readonly revisionSha: string;
}

/**
 * §6's reproduction context, after validation and normalization.
 *
 * `part` and `partKey` are both present or both absent for a component view,
 * and §6.0 explains why one is not enough: `part` is the canonical label that
 * `?part=` carries and the card key is hashed from, and `partKey` is the repo's
 * own key, immune to a change in how we derive labels.
 */
export interface ReportContext {
  readonly siteBuild: string;
  readonly format: ReportFormat;
  readonly metric: ReportMetric;
  readonly part?: string;
  readonly partKey?: string;
  readonly tower?: string;
  readonly selectedTensor?: string;
  readonly comparison?: ReportComparison;
}

/**
 * Everything a visitor asserted, validated and normalized — and nothing else.
 *
 * This is exactly the set §8.4 hashes. Server-derived values (`createdAt`,
 * `status`, `reproductionPath`) are deliberately absent: a digest over a
 * timestamp would differ on every retry, which is the opposite of what
 * idempotency needs.
 */
export interface AcceptedReport {
  readonly schemaVersion: typeof SCHEMA_VERSION;
  readonly submissionId: string;
  readonly repoId: string;
  readonly revisionSha: string;
  readonly problemTypes: readonly ProblemType[];
  readonly details?: string;
  readonly context: ReportContext;
}
